import {property} from '@wonderlandengine/api/decorators.js';

import {
    Container,
    Image,
    Panel,
    Panel9Slice,
    Row,
    Text,
} from '@wonderlandengine/react-ui/components';
import {useEffect, useMemo, useRef, useState} from 'react';
import {
    RotBMaterialContextValue,
    useRotBMaterialContext,
} from '../utils/rotb-material-context.js';
import {parseColor} from '../utils/utils.js';
import {colorSwatch} from '../utils/colorSwatch.js';
import {Align, Justify, PositionType} from '@wonderlandengine/react-ui';
import {useAnimation} from '../hooks/useAnimation.js';
import {Easing} from '@wonderlandengine/tweenjs';
import {NotificationPopupModel} from '../../models/NotificationModel.js';
import {UiTexturesManager} from '../components/ui-textures-manager.js';
import {UnlockIcons} from './unlocked-something.js';
import {Texture, Component} from '@wonderlandengine/api';

export const Notification = () => {
    const rotbMats = useRotBMaterialContext() as RotBMaterialContextValue;
    const material = rotbMats?.windowMaterial;

    const bm = useMemo(() => material!.clone(), []);

    (bm as any).setColor(parseColor(colorSwatch.NotificationBG));
    const [isVisible, setIsVisible] = useState(false);

    const [message, setMessage] = useState('');
    const [icon, setIcon] = useState(null);

    useEffect(() => {
        const change = (prop) => {
            if (prop === 'isVisible') {
                setIsVisible(NotificationPopupModel.instance.isVisible);
                setMessage(NotificationPopupModel.instance.params.popUpDescription);
                setIcon(NotificationPopupModel.instance.params.iconSpriteName);
            }
        };
        NotificationPopupModel.instance.propertyChanged.add(change);

        return () => {
            NotificationPopupModel.instance.propertyChanged.remove(change);
        };
    }, []);

    return (
        <>
            {isVisible && (
                <Container
                    position={PositionType.Absolute}
                    z={0.02}
                    width="100%"
                    alignItems={Align.Center}
                >
                    <Panel9Slice
                        top={75}
                        width={580}
                        material={bm}
                        borderSize={16}
                        borderTextureSize={0.3}
                        height={100}
                        rounding={0}
                        z={0.02}
                    >
                        <Row
                            width="100%"
                            top={20}
                            height={80}
                            justifyContent={Justify.Center}
                        >
                            <Container
                                left={20}
                                width={80}
                                height={80}
                                justifyContent={Justify.Center}
                                alignItems={Align.Center}
                            >
                                <Image
                                    z={0.022}
                                    width={50}
                                    height={50}
                                    rounding={0}
                                    src={
                                        icon
                                            ? AchievementIcons.instance.getIcon(icon)
                                            : UiTexturesManager.instance.info
                                    }
                                ></Image>
                            </Container>
                            <Container
                                width={500}
                                justifyContent={Justify.Center}
                                flexGrow={1}
                            >
                                <Text
                                    height={80}
                                    fontSize={24}
                                    textAlign="center"
                                    z={0.022}
                                >
                                    {message}
                                </Text>
                            </Container>
                        </Row>
                    </Panel9Slice>
                </Container>
            )}
        </>
    );
};

/**
 * unlock-icons
 *
 * These icons map to the texture names in Unity, that's why they
 * have underscores, inconsistand naming and such.
 */
export class AchievementIcons extends Component {
    static TypeName = 'achievement-icons';

    // Singleton
    private static _instance: AchievementIcons;
    static get instance(): AchievementIcons {
        return AchievementIcons._instance;
    }
    @property.texture()
    fallback: Texture;

    @property.texture()
    ach_allnighter: Texture;

    @property.texture()
    ach_beat_cupcake: Texture;

    @property.texture()
    ach_beat_expert: Texture;

    @property.texture()
    ach_beat_fungus: Texture;

    @property.texture()
    ach_beat_normal: Texture;

    @property.texture()
    ach_beat_quickplay: Texture;

    @property.texture()
    ach_beat_skullmode: Texture;

    @property.texture()
    ach_beat_timedmode: Texture;

    @property.texture()
    ach_blobs_away: Texture;

    @property.texture()
    ach_bone_head: Texture;

    @property.texture()
    ach_clockhead: Texture;

    @property.texture()
    ach_color_coordinator: Texture;

    @property.texture()
    ach_cupcake: Texture;

    @property.texture()
    ach_curses: Texture;

    @property.texture()
    ach_da_bomb: Texture;

    @property.texture()
    ach_destroyer_blobs: Texture;

    @property.texture()
    ach_environmentalist: Texture;

    @property.texture()
    ach_expert: Texture;

    @property.texture()
    ach_fashion: Texture;

    @property.texture()
    ach_fun_guy: Texture;

    @property.texture()
    ach_fungicide: Texture;

    @property.texture()
    ach_learned: Texture;

    @property.texture()
    ach_lucky7: Texture;

    @property.texture()
    ach_max_power: Texture;

    @property.texture()
    ach_millionaire: Texture;

    @property.texture()
    ach_moon_blob: Texture;

    @property.texture()
    ach_nature_freak: Texture;

    @property.texture()
    ach_new_challenger: Texture;

    @property.texture()
    ach_normal: Texture;

    @property.texture()
    ach_pucker_up: Texture;

    @property.texture()
    ach_pumkin_pi: Texture;

    @property.texture()
    ach_rank10: Texture;

    @property.texture()
    ach_rank25: Texture;

    @property.texture()
    ach_rank5: Texture;

    @property.texture()
    ach_rank50: Texture;

    @property.texture()
    ach_skull_smasher: Texture;

    @property.texture()
    ach_starcatcher: Texture;

    @property.texture()
    ach_strawberry_b_forever: Texture;

    @property.texture()
    ach_tetromino_maestro: Texture;

    @property.texture()
    ach_tetrominor: Texture;

    @property.texture()
    ach_time_cop: Texture;

    @property.texture()
    ach_tristar: Texture;

    @property.texture()
    ach_truffleshuffle: Texture;

    @property.texture()
    ach_unlock_expert: Texture;

    @property.texture()
    ach_unlockwuk: Texture;

    @property.texture()
    ach_watchmaker: Texture;

    @property.texture()
    ach_waste_rainbow: Texture;

    @property.texture()
    ach_who_made_this_game: Texture;

    @property.texture()
    ach_beat_timemode: Texture;

    @property.texture()
    ach_general_icon: Texture;

    init() {
        if (AchievementIcons._instance) {
            console.error('There can only be one instance of UnlockIcons Component');
        }
        AchievementIcons._instance = this;
    }

    getIcon(icon: string) {
        if (!(icon in this)) {
            console.error(`Icon ${icon} does not exist on UnlockIcons component`);
            return this.fallback;
        }
        return this[icon];
    }
}
